import { classTypes, familyTypes, FilterOperationType } from 'src/lib/constants'
import { IFilteredPlansRequest } from 'src/types/filterPlans.interfaces'

import {
  PLANS_FILTERED,
  PLANS_FILTERED_BULK_EXPORT,
  SHELLS_FILTERED,
  SHELLS_FILTERED_BULK_EXPORT,
  UNITS_FILTERED,
  UNITS_FILTERED_BULK_EXPORT,
} from '../../lib/api-routes'
import { HttpResponse, instance } from '../../lib/axiosInstance'
import { BulkExportResponse } from '../slice/exportedExcelFilesSlice'

// Tracks the in-flight filtered request per endpoint so that when the user
// adds another filter or changes the url parameters, the previous pending
// request for that same endpoint is cancelled and only the latest one wins.
const filteredPlansControllers: Record<string, AbortController> = {}

// On a favorites page the results are restricted to the user's favorited base
// plans. Both the grid and the bulk export have to carry that restriction:
// without it the export writes every plan matching the remaining filters, so it
// came back with far more plans than the page ever showed (LTGC-5647).
// Returns undefined when the page is a favorites page with nothing favorited,
// which means there is no result set to request at all.
export const withFavoritesRestriction = (
  requestDto: IFilteredPlansRequest,
  favorites: string[]
): IFilteredPlansRequest | undefined => {
  const isFavoritesPage = window.location.pathname.includes('/favorites/')

  if (!isFavoritesPage || !requestDto.filters) {
    return requestDto
  }

  if (!favorites?.length) {
    return undefined
  }

  return {
    ...requestDto,
    filters: {
      ...requestDto.filters,
      basePlan: {
        operation: FilterOperationType.IN,
        values: favorites,
      },
    },
  }
}

export const getFilteredPlans = async (
  requestDto: IFilteredPlansRequest,
  favorites: string[]
): Promise<HttpResponse | undefined> => {
  // Determine the appropriate endpoint based on familyType and classTypes
  const endpoint = (() => {
    if (
      requestDto.familyType === familyTypes.MULTI &&
      requestDto.classTypes === classTypes.UNIT
    ) {
      return UNITS_FILTERED
    } else if (
      requestDto.familyType === familyTypes.MULTI &&
      requestDto.classTypes === classTypes.SHELL
    ) {
      return SHELLS_FILTERED
    }
    return PLANS_FILTERED
  })()

  const restrictedRequest = withFavoritesRestriction(requestDto, favorites)
  if (!restrictedRequest) {
    return
  }

  // Cancel any previous in-flight request for this endpoint before starting a
  // new one, so stale responses can't clobber the latest filter selection.
  filteredPlansControllers[endpoint]?.abort()
  const controller = new AbortController()
  filteredPlansControllers[endpoint] = controller

  try {
    return await instance.post(endpoint, restrictedRequest, {
      signal: controller.signal,
    })
  } catch (e) {
    // Re-throw cancellations so the calling thunk can ignore the superseded
    // request instead of overwriting state with an empty/error result.
    if (controller.signal.aborted) {
      throw e
    }
  } finally {
    if (filteredPlansControllers[endpoint] === controller) {
      delete filteredPlansControllers[endpoint]
    }
  }
  return undefined
}

export const getFilteredPlansForBulkExport = async (
  requestDto: IFilteredPlansRequest,
  favorites: string[]
): Promise<BulkExportResponse | null> => {
  try {
    // Determine the appropriate endpoint based on familyType and classTypes
    const endpoint = (() => {
      if (
        requestDto.familyType === familyTypes.MULTI &&
        requestDto.classTypes === classTypes.UNIT
      ) {
        return UNITS_FILTERED_BULK_EXPORT
      } else if (
        requestDto.familyType === familyTypes.MULTI &&
        requestDto.classTypes === classTypes.SHELL
      ) {
        return SHELLS_FILTERED_BULK_EXPORT
      }
      return PLANS_FILTERED_BULK_EXPORT
    })()

    const restrictedRequest = withFavoritesRestriction(requestDto, favorites)
    if (!restrictedRequest) {
      return null
    }

    return await instance
      .post<BulkExportResponse>(endpoint, restrictedRequest)
      .then((res) => res?.data)
  } catch (e) {
    return null
  }
}
